import {
  FormDefinition,
  FormElement,
  FormLogicAction,
  FormLogicRule
} from '../../types/formSchema';
import { FormAnswerMap, evaluateConditionGroup } from './formRuntime';

export const FORM_START_PAGE_ID = '__start__';

export interface FormPage {
  id: string;
  title: string;
  description?: string;
  elements: FormElement[];
}

export const buildFormPages = (definition: FormDefinition): FormPage[] => {
  const pages: FormPage[] = [
    {
      id: FORM_START_PAGE_ID,
      title: definition.title || 'Form',
      description: definition.description,
      elements: []
    }
  ];

  definition.elements.forEach(element => {
    if (element.type === 'PAGE_BREAK') {
      pages.push({
        id: element.id,
        title: element.title || `Page ${pages.length + 1}`,
        description: element.description,
        elements: []
      });
      return;
    }
    pages[pages.length - 1].elements.push(element);
  });

  return pages;
};

export const hasFormFlow = (definition: FormDefinition): boolean =>
  buildFormPages(definition).length > 1 ||
  (definition.logic || []).some(rule =>
    ['GO_TO_PAGE', 'SUBMIT', 'END_WITH_MESSAGE'].includes(rule.action.type)
  );

export const getMatchingPageRule = (
  definition: FormDefinition,
  pageId: string,
  answers: FormAnswerMap
): FormLogicRule | undefined =>
  (definition.logic || []).find(
    rule =>
      rule.sourcePageId === pageId &&
      ['GO_TO_PAGE', 'SUBMIT', 'END_WITH_MESSAGE'].includes(rule.action.type) &&
      evaluateConditionGroup(rule.when, answers)
  );

export const resolveNextPageAction = (
  definition: FormDefinition,
  pageId: string,
  answers: FormAnswerMap
): FormLogicAction | undefined => getMatchingPageRule(definition, pageId, answers)?.action;

export const getSubmissionElementIds = (
  definition: FormDefinition,
  answers: FormAnswerMap
): Set<string> => {
  if (!hasFormFlow(definition)) {
    return new Set(definition.elements.map(element => element.id));
  }

  const pages = buildFormPages(definition);
  const pageById = new Map(pages.map(page => [page.id, page]));
  const result = new Set<string>();
  const visitedPages = new Set<string>();
  let currentPage = pages[0];

  while (currentPage && !visitedPages.has(currentPage.id)) {
    visitedPages.add(currentPage.id);
    currentPage.elements.forEach(element => result.add(element.id));

    const action = resolveNextPageAction(definition, currentPage.id, answers);
    if (action?.type === 'SUBMIT' || action?.type === 'END_WITH_MESSAGE') break;
    if (action?.type === 'GO_TO_PAGE') {
      currentPage = pageById.get(action.targetId);
      continue;
    }

    currentPage = pages[pages.findIndex(page => page.id === currentPage.id) + 1];
  }

  return result;
};
